/**
 * Polls in-app notifications for rule-based chronic business alerts and shows desktop toasts.
 * Thresholds are server-side (env); this only surfaces notifications already created for the user.
 */
import { useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';

const STORAGE_KEY = 'chronicBusinessAlertSeenIds';

export function useChronicBusinessAlerts(enabled: boolean): void {
  const navigate = useNavigate();
  const seenRef = useRef<Set<string>>(new Set());
  const bootRef = useRef(true);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) seenRef.current = new Set(JSON.parse(raw) as string[]);
    } catch {
      /* ignore */
    }
  }, []);

  useQuery(
    ['in-app-notifications-chronic'],
    () => api.get('/api/notifications', { params: { limit: 50, unreadOnly: false } }).then((r) => r.data),
    {
      enabled,
      refetchInterval: 90000,
      refetchIntervalInBackground: true,
      retry: false,
      onSuccess: (data) => {
        const list = data?.notifications || [];
        const chronic = list.filter((n: { type?: string }) => n.type === 'chronic_business');
        if (bootRef.current) {
          chronic.forEach((n: { id: string }) => seenRef.current.add(n.id));
          bootRef.current = false;
          try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(seenRef.current).slice(-400)));
          } catch {
            /* ignore */
          }
          return;
        }
        for (const n of chronic) {
          const row = n as { id: string; title?: string; body?: string };
          if (seenRef.current.has(row.id)) continue;
          seenRef.current.add(row.id);
          try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(seenRef.current).slice(-400)));
          } catch {
            /* ignore */
          }
          notification.warning({
            message: row.title || 'Chronic business alert',
            description: row.body || 'A business is showing repeated issues. Open Analytics for details.',
            duration: 14,
            placement: 'topRight',
            onClick: () => navigate('/analytics'),
          });
        }
      },
    }
  );
}
